// NewCarerModal — types, constants, payload builder, validator.

export const GENDER_OPTIONS = [
  { value: "", label: "Not disclosed" },
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "non_binary", label: "Non-binary" },
];

export const SKILL_OPTIONS = [
  { value: "companion",             label: "Companion" },
  { value: "nursing",               label: "Nursing" },
  { value: "post_discharge",        label: "Post-discharge" },
  { value: "physiotherapy_support", label: "Physiotherapy support" },
  { value: "dementia_care",         label: "Dementia care" },
  { value: "paediatric",            label: "Paediatric" },
] as const;

export const QUAL_OPTIONS = [
  { value: "no_formal_qualification", label: "No formal qualification" },
  { value: "iti_certificate",         label: "ITI Certificate" },
  { value: "gnm_nurse",               label: "GNM Nurse" },
  { value: "bsc_nursing",             label: "BSc Nursing" },
  { value: "anm",                     label: "ANM" },
  { value: "physiotherapist",         label: "Physiotherapist" },
  { value: "social_worker",           label: "Social Worker" },
];

export const TIER_OPTIONS = [
  { value: "1", label: "Tier 1 — Junior" },
  { value: "2", label: "Tier 2 — Senior" },
  { value: "3", label: "Tier 3 — Lead" },
];

export const ONBOARDING_ITEMS = [
  { key: "id_verification",     label: "ID verification" },
  { key: "police_clearance",    label: "Police clearance" },
  { key: "reference_check",     label: "Reference check" },
  { key: "training_completion", label: "Training completion" },
] as const;

export interface FormState {
  full_name: string;
  primary_phone: string;
  city: string;
  gender: string;
  languages_spoken: string[];
  skills: string[];
  qualifications: string[];
  training_completed: string[];
  specialist_skills: string[];
  tier: string;
  coverage_pincodes: string[];
  years_experience: string;
  photo_url: string;
  id_verified: boolean;
  background_verified_date: string;
  contract_expiry_date: string;
  onboarding_checklist: string[];
  agency_id: string;
}

export const INIT: FormState = {
  full_name: "", primary_phone: "", city: "",
  gender: "", languages_spoken: [], skills: [],
  qualifications: [], training_completed: [], specialist_skills: [],
  tier: "1",
  coverage_pincodes: [], years_experience: "", photo_url: "",
  id_verified: false, background_verified_date: "", contract_expiry_date: "",
  onboarding_checklist: [], agency_id: "",
};

export interface FormErrors {
  full_name?: string;
  primary_phone?: string;
  skills?: string;
  background_verified_date?: string;
}

// eslint-disable-next-line complexity, sonarjs/cognitive-complexity -- payload shaper across many optional fields; per-field extraction would relocate without reducing total complexity. Tracked under TASK-N19 Phase B.
export function buildCarerPayload(form: FormState): object {
  return {
    full_name:          form.full_name.trim(),
    primary_phone:      form.primary_phone.trim(),
    city:               form.city.trim() || undefined,
    gender:             form.gender || undefined,
    languages_spoken:   form.languages_spoken.length ? form.languages_spoken : undefined,
    skills:             form.skills,
    qualifications:     form.qualifications.length ? form.qualifications : undefined,
    training_completed: form.training_completed.length ? form.training_completed : undefined,
    specialist_skills:  form.specialist_skills.length ? form.specialist_skills : undefined,
    tier:               Number(form.tier),
    coverage_pincodes:  form.coverage_pincodes.length ? form.coverage_pincodes : undefined,
    years_experience:   form.years_experience ? Number(form.years_experience) : undefined,
    photo_url:          form.photo_url || undefined,
    id_verified:        form.id_verified,
    background_verified_date: form.id_verified && form.background_verified_date ? form.background_verified_date : undefined,
    contract_expiry_date: form.contract_expiry_date || undefined,
    onboarding_checklist: form.onboarding_checklist.length ? form.onboarding_checklist : undefined,
    agency_id:          form.agency_id || undefined,
  };
}

export function validate(f: FormState): FormErrors {
  const errors: FormErrors = {};
  if (!f.full_name.trim())   errors.full_name = "Full name is required.";
  if (!f.primary_phone.trim()) errors.primary_phone = "Phone is required.";
  if (f.skills.length === 0) errors.skills = "Select at least one skill.";
  if (f.id_verified && !f.background_verified_date) errors.background_verified_date = "Required when ID is verified.";
  return errors;
}
